import {
	useRef,
	useEffect,
	useState,
	ReactNode,
	PropsWithChildren
} from 'react';
import styles from './Sidebar.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import { Button, H5 } from '@components';
import { BUTTON_SIZE, BUTTON_TYPES } from '@types';

type FooterAction = {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	showLoader?: boolean;
	buttonType?: BUTTON_TYPES
	buttonSize?: BUTTON_SIZE
}

type SidebarProps = {
	title: string | ReactNode;
	toggleSideBar: (b: boolean) => void;
	open: boolean;
	showFooter?: boolean;
	footerActions?: FooterAction[];
	footerAlignStart?: boolean;
	width?: string;
	canCloseFromOutside?: boolean;
	hasPadding?: boolean;
	isSubSidebar?: boolean;
	right?: string;
	isHeaderVisible?: boolean;
}

export const Sidebar = (props: PropsWithChildren<SidebarProps>) => {
	const {
		title,
		children,
		open,
		toggleSideBar,
		showFooter = false,
		footerActions = null,
		width = '50%',
		canCloseFromOutside = true,
		hasPadding = true,
		footerAlignStart = false,
		isSubSidebar = false,
		right,
		isHeaderVisible = true
	} = props;
	const [listenerAttached, setListenerAttached] = useState(false);
	const closeSidebar = () => {
		toggleSideBar(false);
	}
	const ref = useRef<HTMLDivElement>(null);
	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element;
		if (canCloseFromOutside) {
			if (
				ref.current &&
				!ref.current.contains(target) &&
				// prevent sidebar to close when Select.Option dropdown value is clicked
				!target.closest('[data-testid="@Ofload-Select-Portal"]') &&
				// prevent sidebar to close when item inside modal is clicked
				!target.closest('[data-testid="@Ofload-Modal"]') &&
				// prevent sidebar to close when Typeahead Option value is clicked
				!target.closest('[data-testid="typeahead-list-container"]')
			) {
				closeSidebar();
			}
		}
	};
	useEffect(() => {
		if (!listenerAttached) {
			setListenerAttached(true);
		} else {
			document.removeEventListener('mousedown', handleClickOutside, true);
		}
		document.addEventListener('mousedown', handleClickOutside, true);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside, true);
			setListenerAttached(false);
		}
	}, [canCloseFromOutside]);

	return (
		<div data-testid={'@Ofload-Sidebar'} className={`
			${styles.container}
			${isSubSidebar ? styles.subSidebar : undefined}
			${(open && !isSubSidebar) ? styles.open : undefined}
			${isSubSidebar ? open ? styles.openSubSidebar : styles.closeSubSidebar : undefined}
		`}
		style={{
			width,
			right: `calc(-${width} - 100px)`,
			...(isSubSidebar && { right })
		}}
		ref={ref}
		>
			{
				isHeaderVisible &&
				<div className={styles.header}>
					<div style={{ flex: 1 }}>
						{
							typeof title === 'string' ?
								<H5 text={title} /> :
								title
						}
					</div>
					<button className={styles.closeBtn} onClick={closeSidebar} aria-label={'Close Sidebar'}>
						<CloseIcon style={{ color: 'var(--brand-orange-default)' }} />
					</button>
				</div>
			}
			<div className={`${styles.content} ${hasPadding ? styles.hasPadding : ''} ${showFooter ? styles.contentWithFooter : ''}`}>
				{children}
			</div>
			{showFooter && <div className={`${styles.footer} ${footerAlignStart && styles.footerAlignStart}`}>
				{footerActions !== null && footerActions.map((action, i) => {
					return <Button
						key={i}
						text={action.label}
						onClick={action.onClick}
						disabled={action.disabled !== undefined ? action.disabled : false}
						loading={action.showLoader !== undefined ? action.showLoader : false}
						type={action.buttonType || BUTTON_TYPES.PRIMARY}
						size={action.buttonSize || BUTTON_SIZE.LARGE}
					/>
				})}
			</div>}
		</div>
	);
}
